import { Check } from "lucide-react";

export const Field = ({ label, required, hint, children, testId }) => (
  <div className="space-y-2.5" data-testid={testId}>
    <p className="text-base font-semibold text-slate-900 leading-snug">
      {label} {required && <span className="text-rose-600">*</span>}
    </p>
    {hint && <p className="text-sm text-slate-500 leading-relaxed">{hint}</p>}
    {children}
  </div>
);

const inputCls =
  "w-full h-[52px] sm:h-14 px-4 rounded-xl border-2 border-slate-200 bg-white text-base text-slate-900 placeholder:text-slate-400 shadow-sm transition-colors focus:border-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40";

export const TextField = ({ value, onChange, placeholder, type = "text", testId }) => (
  <input
    data-testid={testId}
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className={inputCls}
  />
);

export const TextAreaField = ({ value, onChange, placeholder, testId, rows = 4 }) => (
  <textarea
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className="w-full px-4 py-3.5 rounded-xl border-2 border-slate-200 bg-white text-base text-slate-900 placeholder:text-slate-400 shadow-sm transition-colors focus:border-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40 resize-y"
  />
);

export const YesNoCards = ({ value, onChange, name, options = ["Yes", "No"] }) => (
  <div className="grid grid-cols-2 gap-3">
    {options.map((opt) => {
      const active = value === opt;
      return (
        <button
          type="button"
          key={opt}
          data-testid={`${name}-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
          aria-pressed={active}
          onClick={() => onChange(opt)}
          className={`relative h-16 sm:h-[72px] rounded-2xl border-2 font-heading font-bold text-lg transition-all duration-200 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40 ${
            active
              ? "border-rose-600 bg-rose-50 text-slate-900 shadow-md"
              : "border-slate-200 bg-white text-slate-600 shadow-sm hover:border-slate-400"
          }`}
        >
          {opt}
          {active && (
            <span className="absolute top-2 right-2 h-5 w-5 rounded-full bg-rose-600 grid place-items-center">
              <Check className="h-3 w-3 text-white" strokeWidth={3.5} />
            </span>
          )}
        </button>
      );
    })}
  </div>
);

export const OptionCards = ({ value, onChange, options, name }) => (
  <div className="grid grid-cols-1 gap-3">
    {options.map((opt) => {
      const v = typeof opt === "string" ? opt : opt.value;
      const label = typeof opt === "string" ? opt : opt.label;
      const hint = typeof opt === "string" ? null : opt.hint;
      const active = value === v;
      return (
        <button
          type="button"
          key={v}
          data-testid={`${name}-option-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
          aria-pressed={active}
          onClick={() => onChange(v)}
          className={`w-full text-left px-4 py-4 rounded-2xl border-2 transition-all duration-200 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40 flex items-center justify-between gap-3 ${
            active
              ? "border-rose-600 bg-rose-50 shadow-md"
              : "border-slate-200 bg-white shadow-sm hover:border-slate-400"
          }`}
        >
          <span>
            <span className="block text-base font-semibold text-slate-900">{label}</span>
            {hint && <span className="block text-sm text-slate-500 mt-0.5">{hint}</span>}
          </span>
          <span
            className={`shrink-0 h-6 w-6 rounded-full border-2 grid place-items-center transition-colors ${
              active ? "bg-rose-600 border-rose-600" : "border-slate-300"
            }`}
          >
            {active && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3.5} />}
          </span>
        </button>
      );
    })}
  </div>
);

export const PillGrid = ({ value, onChange, options, name }) => (
  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
    {options.map((opt) => {
      const active = value === opt;
      return (
        <button
          type="button"
          key={opt}
          data-testid={`${name}-option-${opt.toLowerCase()}`}
          aria-pressed={active}
          onClick={() => onChange(opt)}
          className={`h-12 rounded-xl border-2 font-heading font-bold text-base transition-all duration-200 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40 ${
            active
              ? "border-rose-600 bg-rose-600 text-white shadow-md"
              : "border-slate-200 bg-white text-slate-700 shadow-sm hover:border-slate-400"
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

export const CheckboxCards = ({ values = [], onToggle, options, name }) => (
  <div className="grid grid-cols-1 gap-3">
    {options.map((opt) => {
      const active = values.includes(opt);
      return (
        <button
          type="button"
          key={opt}
          data-testid={`${name}-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
          aria-pressed={active}
          onClick={() => onToggle(opt)}
          className={`w-full text-left px-4 py-4 rounded-2xl border-2 transition-all duration-200 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40 flex items-center gap-3.5 ${
            active
              ? "border-rose-600 bg-rose-50 shadow-md"
              : "border-slate-200 bg-white shadow-sm hover:border-slate-400"
          }`}
        >
          <span
            className={`shrink-0 h-6 w-6 rounded-lg border-2 grid place-items-center transition-colors ${
              active ? "bg-rose-600 border-rose-600" : "border-slate-300 bg-white"
            }`}
          >
            {active && <Check className="h-4 w-4 text-white" strokeWidth={3.5} />}
          </span>
          <span className="text-base font-semibold text-slate-900">{opt}</span>
        </button>
      );
    })}
  </div>
);
